import {
  unwrapFieldValue,
  withFieldOrigin,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import {
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import { z } from 'zod'

import { coerceBranchValue } from './stepBranching'

export const finalEligibilityConfigSchema = z.object({
  /** Ticket field that holds the final eligibility. */
  field: z.string().min(1),
  /** Boolean fields where `false` screens the asset out, whatever screening says. */
  screenOutFields: z.array(z.string().min(1)).min(1),
  /** Field holding the screening decision, used when nothing screened the asset out. */
  decisionField: z.string().min(1),
})

export type FinalEligibilityConfig = z.infer<
  typeof finalEligibilityConfigSchema
>

/** Safely parses `globalContext.finalEligibility`; invalid config reads as absent. */
export function parseFinalEligibilityConfig(
  globalContext: Record<string, unknown>
): FinalEligibilityConfig | undefined {
  const raw = globalContext.finalEligibility
  if (!raw || typeof raw !== 'object') return undefined
  const result = finalEligibilityConfigSchema.safeParse(raw)
  if (!result.success) {
    console.warn(
      '[Libra] finalEligibility config failed validation:',
      result.error.issues
    )
    return undefined
  }
  return result.data
}

/**
 * Any screen-out field that is `false` wins. A missing or true one has no
 * effect, since tickets that predate pre-screening carry none. Otherwise the
 * decision field decides, and `null` means no decision yet.
 */
export function resolveFinalEligibility(
  config: FinalEligibilityConfig,
  fieldValues: FieldValues
): EligibilityStatus | null {
  const screenedOut = config.screenOutFields.some(
    (key) => coerceBranchValue(unwrapFieldValue(fieldValues[key])) === false
  )
  if (screenedOut) return ELIGIBILITY_STATUS.NOT_ELIGIBLE

  const decision = coerceBranchValue(
    unwrapFieldValue(fieldValues[config.decisionField])
  )
  if (decision === true) return ELIGIBILITY_STATUS.ELIGIBLE
  if (decision === false) return ELIGIBILITY_STATUS.NOT_ELIGIBLE
  return null
}

/**
 * Returns the same object when the stored final eligibility already matches.
 * A pending result is written as a `null` value rather than omitted, because
 * writes merge and a missing key would keep the stale value. The value is
 * derived, so it is marked `system` like other computed results.
 */
export function applyFinalEligibility(
  config: FinalEligibilityConfig,
  fieldValues: FieldValues
): FieldValues {
  const resolved = resolveFinalEligibility(config, fieldValues)
  const stored = unwrapFieldValue(fieldValues[config.field]) ?? null
  if (stored === resolved) return fieldValues
  return {
    ...fieldValues,
    [config.field]: withFieldOrigin(wrapFieldValue(resolved), 'system'),
  }
}
